import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_ENDPOINTS, API_BASE_URL } from '../config';
import { FEATURE_ACCESS_CHANGED_EVENT } from '../hooks/useFeatureAccess';
import { useAuth } from '../contexts/AuthContext';
import { IconAlert, IconTrash } from '../components/Icons';
import TierComparisonMatrix from '../components/TierComparisonMatrix';
import { ENTITY_CHANGED_EVENT } from '../utils/activeEntity';
import './AdminFeedback.css';
import './AdminUsers.css';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { usersPagePreviewRules } from '../utils/adminViewAs';


// Delegates to the shared helper so the active-entity header (X-RiskLo-Entity)
// rides along. Building the Authorization header locally silently dropped it,
// which made every admin screen query Vector no matter which brand was selected.
function authHeaders() {
  return { 'Content-Type': 'application/json', ...getBearerAuthHeaders() };
}

function brandAccessSummary(detail, brandAccess) {
  if (brandAccess?.label) return brandAccess.label;
  if (!detail) return 'Select brands';
  if (detail.allBrands) return 'All brands';
  if (!detail.entitySlugs?.length) return 'Select brands';
  return detail.entitySlugs.join(', ');
}

function BrandAccessPicker({
  entities,
  value,
  onChange,
  onSave,
  disabled,
  readOnly,
  saving,
  allowAllBrands = true,
}) {
  const [open, setOpen] = useState(false);
  const [draftAllBrands, setDraftAllBrands] = useState(Boolean(value?.allBrands));
  const [draftSlugs, setDraftSlugs] = useState(value?.entitySlugs || []);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  useEffect(() => {
    setDraftAllBrands(Boolean(value?.allBrands));
    setDraftSlugs(Array.isArray(value?.entitySlugs) ? [...value.entitySlugs] : []);
  }, [value?.allBrands, value?.entitySlugs]);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setMenuStyle({
      top: rect.bottom + 6,
      left: rect.left,
      minWidth: Math.max(rect.width, 210),
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (
        triggerRef.current?.contains(event.target)
        || menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggleSlug = (slug) => {
    setDraftSlugs((prev) => (
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug].sort()
    ));
  };

  const applyDraft = async () => {
    const next = draftAllBrands
      ? { allBrands: true, entitySlugs: [] }
      : { allBrands: false, entitySlugs: [...draftSlugs] };
    if (!next.allBrands && !next.entitySlugs.length) return;
    if (onSave) {
      await onSave(next);
    } else if (onChange) {
      onChange(next);
    }
    setOpen(false);
  };

  if (readOnly) {
    return <BrandAccessBadge brandAccess={value?.brandAccess} />;
  }

  const summary = brandAccessSummary(value, value?.brandAccess);
  const scopeClass = value?.brandAccess?.scope === 'all'
    ? 'admin-users-brand-badge--all'
    : value?.brandAccess?.scope === 'env'
      ? 'admin-users-brand-badge--env'
      : value?.brandAccess?.scope === 'brands'
        ? 'admin-users-brand-badge--multi'
        : `admin-users-brand-badge--${value?.brandAccess?.slug || 'unknown'}`;

  const menu = open && menuStyle
    ? createPortal(
      <div
        ref={menuRef}
        className="admin-users-brand-picker-menu admin-users-brand-picker-menu--portal"
        role="listbox"
        style={{
          position: 'fixed',
          top: menuStyle.top,
          left: menuStyle.left,
          minWidth: menuStyle.minWidth,
        }}
      >
        <label className="admin-users-brand-picker-option admin-users-brand-picker-option--all">
          <input
            type="checkbox"
            checked={draftAllBrands}
            disabled={!allowAllBrands}
            onChange={(e) => {
              if (!allowAllBrands) return;
              setDraftAllBrands(e.target.checked);
              if (e.target.checked) setDraftSlugs([]);
            }}
          />
          <span>All brands</span>
        </label>
        {allowAllBrands ? <div className="admin-users-brand-picker-divider" /> : null}
        {entities.map((entity) => (
          <React.Fragment key={entity.slug}>
            <label className={`admin-users-brand-picker-option${entity.home ? ' admin-users-brand-picker-option--home' : ''}`}>
              <input
                type="checkbox"
                checked={!draftAllBrands && draftSlugs.includes(entity.slug)}
                disabled={draftAllBrands}
                onChange={() => toggleSlug(entity.slug)}
              />
              <span>
                {entity.displayName || entity.slug}
                {entity.home ? <small className="admin-users-brand-picker-sub">Not part of any company</small> : null}
              </span>
            </label>
            {entity.home ? <div className="admin-users-brand-picker-divider" /> : null}
          </React.Fragment>
        ))}
        <div className="admin-users-brand-picker-actions">
          <button
            type="button"
            className="admin-users-btn admin-users-btn--primary"
            onClick={applyDraft}
            disabled={saving || (!draftAllBrands && draftSlugs.length === 0)}
          >
            {saving ? 'Saving…' : 'Apply'}
          </button>
          <button type="button" className="admin-users-btn" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="admin-users-brand-picker">
      <button
        ref={triggerRef}
        type="button"
        className={`admin-users-brand-picker-trigger admin-users-brand-badge ${scopeClass}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled || saving}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Choose which brands this user can access"
      >
        {summary}
      </button>
      {menu}
    </div>
  );
}

function BrandAccessBadge({ brandAccess }) {
  if (!brandAccess) return null;
  const scopeClass = brandAccess.scope === 'all'
    ? 'admin-users-brand-badge--all'
    : brandAccess.scope === 'env'
      ? 'admin-users-brand-badge--env'
      : `admin-users-brand-badge--${brandAccess.slug || 'unknown'}`;
  return (
    <span
      className={`admin-users-brand-badge ${scopeClass}`}
      title={brandAccess.title || brandAccess.label}
    >
      {brandAccess.label}
    </span>
  );
}

function EmailCell({ row }) {
  return (
    <td>
      <div className="admin-users-email-cell">
        <span className="admin-users-email">{row.email}</span>
        {row.source === 'env' && (
          <span className="admin-users-source-badge" title="Railway environment variable">env</span>
        )}
        {row.source === 'excluded' && (
          <span className="admin-users-source-badge admin-users-source-badge--muted">hidden</span>
        )}
      </div>
    </td>
  );
}

function EmailRow({
  row,
  listKey,
  entities,
  supportsBrandPicker,
  canEdit,
  canRemove,
  editorBrandScope,
  onChanged,
  onError,
}) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(row.email);
  const [note, setNote] = useState(row.note || '');
  const [busy, setBusy] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);

  useEffect(() => {
    setEmail(row.email);
    setNote(row.note || '');
  }, [row.email, row.note]);

  const saveBrandAccess = async (nextAccess) => {
    setBrandSaving(true);
    onError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmailBrandAccess(listKey), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          email: row.email,
          allBrands: nextAccess.allBrands,
          entitySlugs: nextAccess.entitySlugs,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to update brand access');
      onChanged();
    } catch (e) {
      onError(e.message);
    } finally {
      setBrandSaving(false);
    }
  };

  const canEditBrandAccess = supportsBrandPicker
    && canEdit
    && row.source === 'db'
    && (editorBrandScope?.allBrands
      || (editorBrandScope?.entitySlugs?.length && !row.brandAccessDetail?.allBrands));

  const brandAccessCell = canEditBrandAccess ? (
    <BrandAccessPicker
      entities={entities}
      allowAllBrands={Boolean(editorBrandScope?.allBrands)}
      value={{
        allBrands: row.brandAccessDetail?.allBrands,
        entitySlugs: editorBrandScope?.allBrands
          ? (row.brandAccessDetail?.entitySlugs || [])
          : (row.brandAccessDetail?.entitySlugs || []).filter(
            (slug) => editorBrandScope.entitySlugs.includes(slug),
          ),
        brandAccess: row.brandAccess,
      }}
      onSave={saveBrandAccess}
      disabled={busy}
      saving={brandSaving}
    />
  ) : (
    <BrandAccessBadge brandAccess={row.brandAccess} />
  );

  const saveEdit = async () => {
    setBusy(true);
    onError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmailById(listKey, row.id), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ email, note }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to update');
      setEditing(false);
      onChanged();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!canRemove) return;
    if (!window.confirm(`Remove ${row.email} from this list?`)) return;
    setBusy(true);
    onError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmailById(listKey, row.id), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to remove');
      onChanged();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const importEnv = async () => {
    setBusy(true);
    onError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmailImportEnv(listKey), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: row.email }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to import');
      onChanged();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const excludeEnv = async () => {
    if (!canRemove) return;
    if (!window.confirm(`Remove ${row.email} from this tier? (Hides Railway env entry without editing Railway.)`)) return;
    setBusy(true);
    onError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmailExcludeEnv(listKey), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: row.email }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to remove');
      onChanged();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const restoreEnv = async () => {
    setBusy(true);
    onError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmailRestoreEnv(listKey), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: row.email }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to restore');
      onChanged();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (row.source === 'excluded') {
    return (
      <tr className="admin-users-row admin-users-row--excluded">
        <EmailCell row={row} />
        <td>{brandAccessCell}</td>
        <td className="admin-users-note">{row.note || '—'}</td>
        <td className="admin-users-actions">
          {canEdit ? (
            <button type="button" className="admin-users-btn" onClick={restoreEnv} disabled={busy}>
              Restore
            </button>
          ) : (
            <span className="admin-users-readonly-hint">Hidden from env</span>
          )}
        </td>
      </tr>
    );
  }

  if (row.readOnly) {
    return (
      <tr className="admin-users-row admin-users-row--env">
        <EmailCell row={row} />
        <td>{brandAccessCell}</td>
        <td className="admin-users-note">—</td>
        <td className="admin-users-actions">
          {canEdit ? (
            <>
              {canRemove ? (
                <button type="button" className="admin-users-btn admin-users-btn--danger" onClick={excludeEnv} disabled={busy}>
                  Remove from list
                </button>
              ) : null}
              <button type="button" className="admin-users-btn" onClick={importEnv} disabled={busy}>
                Import to DB
              </button>
            </>
          ) : (
            <span className="admin-users-readonly-hint">Railway env only</span>
          )}
        </td>
      </tr>
    );
  }

  if (editing && canEdit) {
    return (
      <tr className="admin-users-row admin-users-row--editing">
        <td>
          <input
            className="admin-users-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </td>
        <td>{brandAccessCell}</td>
        <td>
          <input
            className="admin-users-input"
            type="text"
            value={note}
            placeholder="Optional note"
            onChange={(e) => setNote(e.target.value)}
          />
        </td>
        <td className="admin-users-actions">
          <button type="button" className="admin-users-btn admin-users-btn--primary" onClick={saveEdit} disabled={busy}>
            Save
          </button>
          <button type="button" className="admin-users-btn" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="admin-users-row">
      <EmailCell row={row} />
      <td>{brandAccessCell}</td>
      <td className="admin-users-note">{row.note || '—'}</td>
      <td className="admin-users-actions">
        {canEdit ? (
          <>
            <button type="button" className="admin-users-btn" onClick={() => setEditing(true)} disabled={busy}>
              Edit
            </button>
            {canRemove ? (
              <button
                type="button"
                className="admin-users-btn admin-users-btn--danger"
                onClick={remove}
                disabled={busy}
                aria-label={`Remove ${row.email}`}
              >
                <IconTrash size={14} />
              </button>
            ) : null}
          </>
        ) : (
          <span className="admin-users-readonly-hint">View only</span>
        )}
      </td>
    </tr>
  );
}

function TierListSection({
  list,
  entities: brandEntities,
  homeBrand = null,
  editorBrandScope,
  canEdit,
  canRemove,
  entitySlug,
  entityDisplayName,
  onChanged,
  onError,
  isFullAdmin = false,
}) {
  const [newEmail, setNewEmail] = useState('');
  const [moving, setMoving] = useState(false);
  // Basic / Pro / Max can also be given as RiskLo (self-use): no company.
  const entities = homeBrand ? [homeBrand, ...brandEntities] : brandEntities;

  // One-time move of everyone on Dev Max to Sales (Full Admin). Dev Max stays.
  const moveTarget = list.key === 'DEV_MODE_MAX_EMAILS' ? { key: 'SALES_EMAILS', label: 'Sales' } : null;
  const moveAll = async () => {
    if (!moveTarget) return;
    const ok = window.confirm(
      `Move everyone on ${list.label} to ${moveTarget.label}, on every brand?\n\n`
      + `They keep exactly the same access. ${list.label} stays, just empty, and you can still add people to it.`,
    );
    if (!ok) return;
    setMoving(true);
    onError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmailMoveAll(list.key), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ toListKey: moveTarget.key }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to move emails');
      onChanged();
    } catch (err) {
      onError(err.message);
    } finally {
      setMoving(false);
    }
  };
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  // The brand a new email starts on: the one being viewed. Viewing RiskLo, the
  // staff lists (no self-use option) start on the first company brand instead.
  const viewedSlug = entitySlug || 'vector';
  const startSlug = entities.some((e) => e.slug === viewedSlug) ? viewedSlug : (entities[0]?.slug || viewedSlug);
  const [newBrandAccess, setNewBrandAccess] = useState({
    allBrands: false,
    entitySlugs: [startSlug],
  });

  useEffect(() => {
    const defaultSlugs = editorBrandScope?.allBrands
      ? [startSlug]
      : (editorBrandScope?.entitySlugs?.length
        ? [editorBrandScope.entitySlugs[0]]
        : [startSlug]);
    setNewBrandAccess({
      allBrands: false,
      entitySlugs: defaultSlugs,
    });
  }, [startSlug, list.key, editorBrandScope]);

  const addEmail = async (e) => {
    e.preventDefault();
    // Several at once: commas, spaces, semicolons or new lines between emails.
    const emails = [...new Set(newEmail.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean))];
    if (!emails.length) return;
    const invalid = emails.filter((email) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email));
    if (invalid.length) {
      onError(`Not a valid email: ${invalid.join(', ')}`);
      return;
    }
    if (!newBrandAccess.allBrands && !newBrandAccess.entitySlugs?.length) {
      onError('Select at least one brand or choose All brands.');
      return;
    }
    setAdding(true);
    onError(null);
    const failed = [];
    for (const email of emails) {
      try {
        const res = await fetch(API_ENDPOINTS.adminTierEmailsList(list.key), {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            email,
            note: newNote.trim() || null,
            allBrands: newBrandAccess.allBrands,
            entitySlugs: newBrandAccess.entitySlugs,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to add email');
      } catch (err) {
        failed.push({ email, error: err.message });
      }
    }
    // Leave only the ones that didn't go in, so they can be fixed and retried.
    setNewEmail(failed.map((f) => f.email).join(', '));
    if (failed.length) {
      onError(failed.map((f) => `${f.email}: ${f.error}`).join(' · '));
    } else {
      setNewNote('');
      setNewBrandAccess({
        allBrands: false,
        entitySlugs: [startSlug],
      });
    }
    if (failed.length < emails.length) onChanged();
    setAdding(false);
  };

  return (
    <section className="admin-users-section">
      <header className="admin-users-section-header">
        <div>
          <h3>{list.label}</h3>
          <p className="admin-users-description">{list.description}</p>
        </div>
        <span className="admin-users-count">
          {list.emails.length} email{list.emails.length === 1 ? '' : 's'}
          {moveTarget && isFullAdmin && list.emails.length > 0 ? (
            <button type="button" className="admin-users-move-all" onClick={moveAll} disabled={moving}>
              {moving ? 'Moving…' : `Move everyone to ${moveTarget.label}`}
            </button>
          ) : null}
        </span>
      </header>

      {canEdit && (
        <form className="admin-users-add-form" onSubmit={addEmail}>
          <p className="admin-users-add-hint">
            {list.supportsBrandPicker !== false ? (
              editorBrandScope?.allBrands ? (
                <>
                  Choose <strong>All brands</strong> or specific brands below when adding someone.
                  {homeBrand ? <> Pick <strong>RiskLo (self-use)</strong> for someone who isn’t part of a company.</> : null}
                </>
              ) : (
                <>You can assign access only for brands you manage ({editorBrandScope?.entitySlugs?.join(', ') || entityDisplayName}).</>
              )
            ) : (
              <>New Admin emails apply to <strong>All brands</strong> automatically.</>
            )}
          </p>
          <div className="admin-users-add-fields">
          <input
            className="admin-users-input"
            type="text"
            placeholder="Add email(s), separated by commas"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
          />
          <input
            className="admin-users-input"
            type="text"
            placeholder="Note (optional)"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          {list.supportsBrandPicker !== false ? (
            <BrandAccessPicker
              entities={entities}
              allowAllBrands={Boolean(editorBrandScope?.allBrands)}
              value={{
                ...newBrandAccess,
                brandAccess: newBrandAccess.allBrands
                  ? { scope: 'all', label: 'All brands' }
                  : {
                    scope: 'brands',
                    label: newBrandAccess.entitySlugs
                      .map((slug) => entities.find((e) => e.slug === slug)?.displayName || slug)
                      .join(', ') || entityDisplayName,
                  },
              }}
              onChange={setNewBrandAccess}
              disabled={adding}
            />
          ) : null}
          <button type="submit" className="admin-users-btn admin-users-btn--primary" disabled={adding}>
            {adding ? 'Adding…' : 'Add'}
          </button>
          </div>
        </form>
      )}

      <div className="admin-users-table-wrap">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Brand access</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.emails.length === 0 ? (
              <tr>
                <td colSpan={4} className="admin-users-empty">No users in this list for {entityDisplayName || 'this brand'}.</td>
              </tr>
            ) : (
              list.emails.map((row) => (
                <EmailRow
                  key={row.id ? `db-${row.id}` : `env-${row.email}`}
                  row={row}
                  listKey={list.key}
                  entities={entities}
                  supportsBrandPicker={list.supportsBrandPicker !== false}
                  canEdit={canEdit}
                  canRemove={canRemove}
                  editorBrandScope={editorBrandScope}
                  onChanged={onChanged}
                  onError={onError}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {list.excluded?.length > 0 && (
        <div className="admin-users-excluded-block">
          <p className="admin-users-excluded-title">Hidden from Railway env ({list.excluded.length})</p>
          <table className="admin-users-table">
            <tbody>
              {list.excluded.map((row) => (
                <EmailRow
                  key={`excluded-${row.email}`}
                  row={row}
                  listKey={list.key}
                  entities={entities}
                  supportsBrandPicker={false}
                  canEdit={canEdit}
                  canRemove={canRemove}
                  editorBrandScope={editorBrandScope}
                  onChanged={onChanged}
                  onError={onError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Which environment this database belongs to, judged by the API host. */
function environmentLabel() {
  const host = String(API_BASE_URL || '');
  if (/staging/i.test(host)) return 'Staging';
  if (/localhost|127\.0\.0\.1|192\.168\./i.test(host)) return 'Local';
  return 'Production';
}

/**
 * Full Admin only: switch features on or off per tier. Stored in this
 * environment's database, so staging and production are set separately.
 *
 * "All brands" edits the global setting. "<Brand> only" gives the brand being
 * viewed its own setting for a feature (e.g. a client who doesn't want RiskLo
 * AI); a feature without one follows all brands.
 */
export function FeatureAccessPanel({ entitySlug = null, entityName = '' }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('all'); // 'all' | 'brand'
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const brandScope = scope === 'brand' && Boolean(entitySlug);
  const brandName = entityName || entitySlug;

  const load = useCallback(async () => {
    try {
      const url = brandScope
        ? `${API_ENDPOINTS.adminFeatureAccess}?entity=${encodeURIComponent(entitySlug)}`
        : API_ENDPOINTS.adminFeatureAccess;
      const res = await fetch(url, { headers: authHeaders() });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Failed to load feature access');
      setData(body);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [brandScope, entitySlug]);

  useEffect(() => { setData(null); load(); }, [load]);

  const save = async (feature, request) => {
    setSavingKey(feature.key);
    setSavedKey(null);
    try {
      const res = await fetch(request.url, { method: request.method, headers: authHeaders(), ...(request.body ? { body: JSON.stringify(request.body) } : {}) });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Save failed');
      setData(body);
      setSavedKey(feature.key);
      window.dispatchEvent(new Event(FEATURE_ACCESS_CHANGED_EVENT));
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  const setTiers = (feature, tiers) => save(feature, {
    method: 'PUT',
    url: API_ENDPOINTS.adminFeatureAccessUpdate(feature.key),
    body: brandScope ? { tiers, entitySlug } : { tiers },
  });

  const toggle = (feature, tierId) => setTiers(feature, feature.tiers.includes(tierId)
    ? feature.tiers.filter((t) => t !== tierId)
    : [...feature.tiers, tierId]);

  const followAllBrands = (feature) => save(feature, {
    method: 'DELETE',
    url: `${API_ENDPOINTS.adminFeatureAccessUpdate(feature.key)}/brand/${encodeURIComponent(entitySlug)}`,
  });

  const env = environmentLabel();
  // RiskLo-wide switches live on Admin → Brands, not per tier here.
  const features = (data?.features || []).filter((f) => f.group !== 'Global');
  const hiddenEverywhere = features.filter((f) => !f.tiers.length).length;
  const ownSettings = brandScope ? features.filter((f) => f.brandOverride).length : 0;
  const groups = features.reduce((acc, f) => {
    const g = f.group || 'Other';
    if (!acc.some((x) => x.name === g)) acc.push({ name: g, items: [] });
    acc.find((x) => x.name === g).items.push(f);
    return acc;
  }, []);
  const colCount = 2 + (data?.tiers?.length || 0);

  return (
    <section className={`admin-users-section admin-users-features${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="admin-users-features-toggle"
        aria-expanded={open}
        aria-controls="admin-users-features-body"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="admin-users-features-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="admin-users-features-heading">
          <h3>Feature access</h3>
          <span className="admin-users-description">
            {data
              ? `${features.length} features · ${hiddenEverywhere ? `${hiddenEverywhere} hidden from everyone but Admin` : 'all visible to at least one tier'}`
                + (brandScope ? ` · ${ownSettings} set for ${brandName} only` : '')
              : 'Which tiers can see each Results and Risk Tools page'}
          </span>
        </span>
        <span className={`admin-users-env-pill admin-users-env-pill--${env.toLowerCase()}`}>{env}</span>
      </button>

      {open ? (
        <div id="admin-users-features-body" className="admin-users-features-body">
          <p className="admin-users-description">
            Choose which tiers can see each page. Full Admin always can. Switches only <strong>hide</strong> pages — a tier
            still needs the access it already has to use them (e.g. Pro for Blowout Risk). These settings apply to{' '}
            <strong>{env}</strong> only, so set staging and production separately.
          </p>

          {entitySlug ? (
            <div className="admin-users-features-scope" role="tablist" aria-label="Which brands these settings apply to">
              <button type="button" role="tab" aria-selected={!brandScope} className={!brandScope ? 'active' : ''} onClick={() => setScope('all')}>
                All brands
              </button>
              <button type="button" role="tab" aria-selected={brandScope} className={brandScope ? 'active' : ''} onClick={() => setScope('brand')}>
                {brandName} only
              </button>
            </div>
          ) : null}
          {brandScope ? (
            <p className="admin-users-description">
              Changes here apply to <strong>{brandName}</strong> only and replace the all-brands setting for that feature.
              A feature turned off for a tier here is removed for them on {brandName}, with no upgrade prompt.
            </p>
          ) : null}

          {error ? (
            <div className="admin-users-error" role="alert"><IconAlert size={16} /><span>{error}</span></div>
          ) : null}
          {!data && !error ? <p className="admin-users-loading">Loading feature access…</p> : null}

          {data ? (
            <div className="admin-users-features-table-wrap">
              <table className="admin-users-features-table">
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">Admin</th>
                    {data.tiers.map((t) => <th key={t.id} scope="col">{t.label}</th>)}
                  </tr>
                </thead>
                {groups.map((group) => (
                  <tbody key={group.name}>
                    <tr className="admin-users-features-group">
                      <th scope="colgroup" colSpan={colCount}>{group.name}</th>
                    </tr>
                    {group.items.map((f) => (
                      <tr key={f.key}>
                        <th scope="row">
                          <span className="admin-users-feature-name">{f.label}</span>
                          {f.description ? <span className="admin-users-feature-desc">{f.description}</span> : null}
                          <span className="admin-users-feature-status" role="status">
                            {savingKey === f.key ? 'Saving…'
                              : savedKey === f.key ? 'Saved'
                                : brandScope && f.brandOverride ? (!f.tiers.length ? `Off for ${brandName}` : `Set for ${brandName} only`)
                                  : brandScope ? 'Follows all brands'
                                    : !f.tiers.length ? 'Hidden from everyone but Admin'
                                      : f.isDefault ? 'Default' : ''}
                          </span>
                          {brandScope ? (
                            <span className="admin-users-feature-brand-actions">
                              {f.tiers.length ? (
                                <button type="button" className="admin-users-link-btn" disabled={savingKey === f.key} onClick={() => setTiers(f, [])}>
                                  Turn off for {brandName}
                                </button>
                              ) : null}
                              {f.brandOverride ? (
                                <button type="button" className="admin-users-link-btn" disabled={savingKey === f.key} onClick={() => followAllBrands(f)}>
                                  Use the all-brands setting
                                </button>
                              ) : null}
                            </span>
                          ) : null}
                        </th>
                        <td><input type="checkbox" checked disabled aria-label={`${f.label}: Admin always`} /></td>
                        {data.tiers.map((t) => (
                          <td key={t.id}>
                            <input
                              type="checkbox"
                              checked={f.tiers.includes(t.id)}
                              disabled={savingKey === f.key || (f.neverTiers || []).includes(t.id)}
                              onChange={() => toggle(f, t.id)}
                              aria-label={`${f.label}: ${t.label}`}
                              title={(f.neverTiers || []).includes(t.id) ? `${f.label} can’t be turned on for ${t.label}` : undefined}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default function AdminUsers({ onNavigate }) {
  const { isAdmin, isVectorAdminPlus, adminViewAs, isAdminActual } = useAuth();
  // Admin previewing a lower tier: apply that tier's rules (the server still sees Admin).
  const previewRules = usersPagePreviewRules(adminViewAs, isAdminActual);
  const [lists, setLists] = useState([]);
  const [entities, setEntities] = useState([]);
  const [entitySlug, setEntitySlug] = useState('vector');
  const [entityDisplayName, setEntityDisplayName] = useState('');
  const [isVectorBrand, setIsVectorBrand] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [canRemove, setCanRemove] = useState(false);
  const [editorBrandScope, setEditorBrandScope] = useState({ allBrands: true, entitySlugs: [] });
  const [editableEntities, setEditableEntities] = useState([]);
  const [homeBrand, setHomeBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const loadLists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminTierEmails, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load users');
      setLists(data.lists || []);
      setEntities(data.entities || []);
      setEditableEntities(data.editableEntities || data.entities || []);
      setHomeBrand(data.homeBrand || null);
      setEditorBrandScope(data.editorBrandScope || { allBrands: true, entitySlugs: [] });
      setEntitySlug(data.entitySlug || 'vector');
      setEntityDisplayName(data.entityDisplayName || data.entitySlug || '');
      setIsVectorBrand(data.isVectorBrand !== false);
      setCanEdit(Boolean(data.canEdit));
      setCanRemove(Boolean(data.canRemove));
    } catch (e) {
      setError(e.message);
      setLists([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    const onEntityChanged = () => {
      setMessage(null);
      loadLists();
    };
    window.addEventListener(ENTITY_CHANGED_EVENT, onEntityChanged);
    return () => window.removeEventListener(ENTITY_CHANGED_EVENT, onEntityChanged);
  }, [loadLists]);

  return (
    <div className="admin-users-page">
      <header className="admin-users-header">
        <div>
          <span className="admin-users-eyebrow">Admin</span>
          <h1>Users</h1>
          <p className="admin-users-subtitle">
            Tier allowlists for the active brand. Switch brands in the sidebar to manage users for that
            brand — each list shows people with access here (or marked &quot;All brands&quot; when global).
            Use the brand picker to grant Brand Admin and dev tiers on one or many brands.
          </p>
        </div>
        <button type="button" className="admin-users-btn" onClick={loadLists} disabled={loading}>
          Refresh
        </button>
      </header>

      <div className="admin-users-brand-banner" role="status">
        <span className="admin-users-brand-banner-label">Viewing brand</span>
        <span className={`admin-users-brand-banner-name admin-users-brand-badge--${entitySlug}`}>
          {entityDisplayName}
        </span>
        <span className="admin-users-brand-banner-hint">
          {isVectorBrand
            ? 'Railway env-only emails appear here until imported to the database.'
            : 'Lists show users scoped to this brand or global Admin. Add Brand Admin tiers here with the brand picker.'}
        </span>
      </div>

      {!isAdmin && !isVectorAdminPlus && (
        <div className="admin-users-readonly-banner" role="status">
          Brand Admin — you can add people to Support and Sales. Other lists are view only; Admin or Brand Admin Plus can change them.
        </div>
      )}

      {isVectorAdminPlus && canEdit && (!canRemove || previewRules) && (
        <div className="admin-users-readonly-banner admin-users-readonly-banner--plus" role="status">
          Brand Admin Plus — you can add and edit allowlist emails on this page (except the Admin list), but not remove them.
          {!editorBrandScope.allBrands && editorBrandScope.entitySlugs?.length ? (
            <> Brand access changes are limited to: {editorBrandScope.entitySlugs.join(', ')}.</>
          ) : null}
        </div>
      )}

      {message && (
        <div className="admin-users-message" role="status">{message}</div>
      )}
      {error && (
        <div className="admin-users-error" role="alert">
          <IconAlert size={16} />
          <span>{error}</span>
        </div>
      )}

      {isAdmin ? <FeatureAccessPanel entitySlug={entitySlug} entityName={entityDisplayName} /> : null}

      {loading ? (
        <p className="admin-users-loading">Loading tier allowlists…</p>
      ) : (
        <div className="admin-users-sections">
          {lists
            .filter((list) => !previewRules?.hiddenListKeys.includes(list.key))
            .map((list) => (
            <TierListSection
              key={list.key}
              list={list}
              entities={editableEntities.length ? editableEntities : entities}
              homeBrand={list.allowsHomeBrand ? homeBrand : null}
              editorBrandScope={editorBrandScope}
              entitySlug={entitySlug}
              entityDisplayName={entityDisplayName}
              canEdit={previewRules
                ? Boolean(list.canEdit ?? canEdit) && previewRules.canEditList(list.key)
                : (list.canEdit ?? canEdit)}
              canRemove={previewRules ? previewRules.canRemove : (list.canRemove ?? canRemove)}
              isFullAdmin={isAdmin}
              onChanged={() => {
                setMessage('Saved — sign out/in or refresh session to update your sidebar badge.');
                loadLists();
              }}
              onError={setError}
            />
          ))}
        </div>
      )}

      <p className="admin-users-footnote">
        Use the <strong>Brand access</strong> picker to grant <strong>All brands</strong> or specific brands.
        {isVectorBrand ? (
          <> Rows tagged <strong>env</strong> come from Railway (this brand only). Use <strong>Import to DB</strong> to manage them per brand.</>
        ) : (
          null
        )}
        {' '}After changing your own tier, sign out and sign back in so the app re-checks Admin / Brand Admin status.
      </p>

      <TierComparisonMatrix viewerTier={isAdmin ? 'admin' : isVectorAdminPlus ? 'vadminPlus' : 'vadmin'} />
    </div>
  );
}

export { TierListSection };
